import React from 'react';
import { Building2, Check, CircleDot, MapPin, Settings2, SlidersHorizontal } from 'lucide-react';
import Alt4Layout from '../components/Alt4Layout';
import { ClosingCta, PrimaryCta, Reveal, SectionIntro } from '../components/Alt4Shared';

const operatingControls = [
  { icon: SlidersHorizontal, title: 'Central policy', body: 'Set shared workflow standards, approval boundaries, and organization-wide operating rules.' },
  { icon: Settings2, title: 'Local configuration', body: 'Adapt schedules, responsibilities, and operating details where a location needs its own setup.' },
  { icon: MapPin, title: 'Location queues', body: 'Give local teams a focused view of work, decisions, and exceptions that need attention.' },
  { icon: Building2, title: 'Portfolio view', body: 'See operating state across the group, then move from the organization to a location without losing context.' },
];

const locations = [
  { label: 'North region', detail: 'Operations on track', tone: 'done' },
  { label: 'Central region', detail: 'Review queue active', tone: 'review' },
  { label: 'South region', detail: 'Workflows running', tone: 'running' },
];

export default function Alt4Practices() {
  return (
    <Alt4Layout title="Multi-location">
        <section className="alt4-page-hero alt4-section">
          <div className="alt4-container alt4-hero-grid alt4-page-hero__grid">
            <Reveal className="alt4-hero-copy">
              <span className="alt4-eyebrow">Multi-location operations</span>
              <h1>One operating model. Every location in view.</h1>
              <p>Create one operational backbone across the group, with the control to standardize what should be shared and preserve flexibility where locations genuinely differ.</p>
              <PrimaryCta />
            </Reveal>
            <Reveal className="alt4-panel alt4-portfolio-panel" delay={0.08}>
              <header>
                <div><span className="alt4-interface-label">Organization view</span><h2>All locations</h2></div>
                <span className="alt4-portfolio-panel__preview-label">Live portfolio preview</span>
              </header>
              <div className="alt4-portfolio-panel__summary">
                <div><span>Shared policies</span><strong>Active</strong></div>
                <div><span>Local workflows</span><strong>In view</strong></div>
                <div><span>Exceptions</span><strong>Routed</strong></div>
              </div>
              <div className="alt4-portfolio-panel__locations">
                {locations.map((location) => (
                  <article key={location.label}>
                    <span className={`alt4-location-state alt4-location-state--${location.tone}`}><CircleDot aria-hidden="true" /></span>
                    <div><strong>{location.label}</strong><small>{location.detail}</small></div>
                    <small>Live state</small>
                  </article>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="alt4-section">
          <div className="alt4-container">
            <SectionIntro
              eyebrow="Standardize with intent"
              title="Share the operating backbone. Keep the right decisions local."
              body="A growing group needs repeatable operations without turning every location into the same office. Asha separates organization policy from local configuration so both remain visible and governable."
            />
            <div className="alt4-grid alt4-grid--four alt4-operating-controls">
              {operatingControls.map(({ icon: Icon, title, body }, index) => (
                <Reveal className="alt4-card" delay={index * 0.05} key={title}>
                  <Icon aria-hidden="true" />
                  <h3>{title}</h3>
                  <p>{body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="alt4-section alt4-section--pale">
          <div className="alt4-container alt4-grid alt4-grid--two alt4-practices-views">
            <SectionIntro
              eyebrow="From emerging to established"
              title="A clear operating view as the group evolves."
              body="Emerging DSOs can establish shared workflows as they add locations. Larger DSOs can scope policy, permissions, reporting, and exception ownership across a more layered organization."
            />
            <div className="alt4-panel alt4-permission-map">
              <span className="alt4-interface-label">Permission scope</span>
              <div className="alt4-permission-map__org"><Building2 aria-hidden="true" /><span><strong>Organization</strong><small>Shared policy and visibility</small></span></div>
              <div className="alt4-permission-map__branches">
                <article><MapPin aria-hidden="true" /><span><strong>Region</strong><small>Scoped oversight</small></span><Check aria-hidden="true" /></article>
                <article><MapPin aria-hidden="true" /><span><strong>Location</strong><small>Local configuration</small></span><Check aria-hidden="true" /></article>
                <article><Settings2 aria-hidden="true" /><span><strong>Team role</strong><small>Action permissions</small></span><Check aria-hidden="true" /></article>
              </div>
            </div>
          </div>
        </section>

        <section className="alt4-section alt4-section--blue">
          <div className="alt4-container alt4-grid alt4-grid--two alt4-group-visibility">
            <SectionIntro
              inverse
              eyebrow="One source of operating truth"
              title="Move from portfolio signal to local action."
              body="Leaders can see which workflows are moving, where review is needed, and who owns the next decision. Local teams see a clear queue grounded in their location context."
            />
            <div className="alt4-panel alt4-panel--blue alt4-group-visibility__list">
              {['Organization policy applied', 'Location configuration preserved', 'Open work grouped by ownership', 'Exceptions visible at the right scope'].map((item) => (
                <div key={item}><Check aria-hidden="true" /><span>{item}</span></div>
              ))}
            </div>
          </div>
        </section>

        <ClosingCta
          eyebrow="Operate as one group"
          title="See every location without flattening what makes it work."
          body="Walk through shared policy, local configuration, permission scopes, and portfolio visibility."
        />
    </Alt4Layout>
  );
}
